import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  darDeBajaJornadas, listarBajas, listarJornadasParaBaja, reactivarJornada, type BajaVigente,
} from '../api/bajasDeTurnos';
import {
  ETIQUETA_ESTADO, alternarTodo, casillaDeTodo, estadoParaBaja, filaDeBaja, idsParaDarDeBaja, resumenDeSeleccion,
  textoDelResultado, type EstadoParaBaja, type JornadaParaBaja, type ResultadoBaja,
} from '../domain/bajasDeTurnos';
import { FILTROS_VACIOS, hayFiltros, opcionesDe, pasaFiltro, type FiltrosTabla } from '../domain/tableFilters';
import { diaSemanaYFecha } from '../domain/vigenciaHorario';
import BarraFiltros, { FiltroFecha, FiltroSelect, FiltroTexto, type OpcionFiltro } from './BarraFiltros';

/**
 * Pestaña «Bajas» de Asignaciones (0399): el admin da de baja los turnos que el cliente canceló. Diseño en
 * docs/superpowers/specs/2026-11-04-bajas-de-turnos-design.md.
 *
 * Lo que se da de baja se archiva y se borra, así que desaparece solo de Planificación, Asignaciones, Validación, Hoy y
 * los tableros. Un turno con marca de asistencia no se ofrece: se corrige en Validación. La lógica de selección vive en
 * `domain/bajasDeTurnos.ts`, con pruebas.
 */

const hhmm = (t: string) => t.slice(0, 5);

const ESTADOS: EstadoParaBaja[] = ['libre', 'con_persona', 'con_marca'];

/** Cuántos nombres se listan en la confirmación antes de resumir el resto. */
const MAX_NOMBRES = 15;

export default function BajasDeTurnos({ campaignId }: { campaignId: string }) {
  const [jornadas, setJornadas] = useState<JornadaParaBaja[]>([]);
  const [bajas, setBajas] = useState<BajaVigente[]>([]);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');
  const [filtros, setFiltros] = useState<FiltrosTabla>(FILTROS_VACIOS);
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [confirmando, setConfirmando] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState<ResultadoBaja | null>(null);
  const [verBajas, setVerBajas] = useState(false);
  const [aviso, setAviso] = useState('');
  const casillaTodo = useRef<HTMLInputElement>(null);

  const recargar = useCallback(() => {
    setCargando(true);
    setError('');
    Promise.all([listarJornadasParaBaja(campaignId), listarBajas(campaignId)])
      .then(([j, b]) => { setJornadas(j); setBajas(b); })
      .catch((e) => {
        setJornadas([]);
        setError(e instanceof Error ? e.message : 'No se pudieron cargar los turnos.');
      })
      .finally(() => setCargando(false));
  }, [campaignId]);

  useEffect(recargar, [recargar]);
  useEffect(() => {
    setFiltros(FILTROS_VACIOS);
    setSel(new Set());
    setResultado(null);
    setAviso('');
  }, [campaignId]);

  const set = (campo: keyof FiltrosTabla) => (v: string) => setFiltros((f) => ({ ...f, [campo]: v }));
  const filas = useMemo(() => jornadas.map(filaDeBaja), [jornadas]);
  const visibles = useMemo(() => jornadas.filter((j) => pasaFiltro(filaDeBaja(j), filtros)), [jornadas, filtros]);
  const aDarDeBaja = useMemo(() => idsParaDarDeBaja(visibles, sel), [visibles, sel]);
  const estadoTodo = casillaDeTodo(visibles, sel);

  useEffect(() => {
    if (casillaTodo.current) casillaTodo.current.indeterminate = estadoTodo === 'algunas';
  }, [estadoTodo]);

  const opcionesPdv: OpcionFiltro[] = useMemo(() => {
    const m = new Map<string, string>();
    for (const j of jornadas) if (j.pdv) m.set(j.pdv.code, `${j.pdv.code} — ${j.pdv.name}`);
    return [...m].map(([valor, etiqueta]) => ({ valor, etiqueta })).sort((a, b) => a.etiqueta.localeCompare(b.etiqueta, 'es'));
  }, [jornadas]);
  const comoOpciones = (vs: string[]): OpcionFiltro[] => vs.map((v) => ({ valor: v, etiqueta: v }));

  function alternar(id: string) {
    setSel((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }

  async function confirmarBaja() {
    if (motivo.trim() === '' || aDarDeBaja.length === 0) return;
    setEnviando(true);
    setError('');
    try {
      const r = await darDeBajaJornadas(aDarDeBaja, motivo.trim());
      setResultado(r);
      setConfirmando(false);
      setMotivo('');
      setSel(new Set());
      recargar();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo dar de baja.');
    } finally {
      setEnviando(false);
    }
  }

  async function reactivar(b: BajaVigente) {
    const donde = b.pdv ? `${b.pdv.code} — ${b.pdv.name}` : 'la tienda';
    if (!window.confirm(`¿Reactivar el turno del ${diaSemanaYFecha(b.fecha)} en ${donde}? Vuelve abierto y sin persona.`)) return;
    setError('');
    try {
      await reactivarJornada(b.id);
      setAviso(`Turno reactivado: ${diaSemanaYFecha(b.fecha)} en ${donde}. Está abierto en «Posiciones abiertas».`);
      recargar();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo reactivar.');
    }
  }

  const resumen = resumenDeSeleccion(visibles, sel);

  return (
    <div>
      <p className="mb-3 text-sm text-gray-600">
        Para los turnos que el cliente canceló. Al darlos de baja desaparecen de Planificación, Asignaciones,
        Validación y los tableros, y ningún horario los vuelve a crear. Los turnos con marca de asistencia no se dan
        de baja: se corrigen en Validación.
      </p>
      {aviso && <p className="mb-3 rounded border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">{aviso}</p>}
      {error && <p className="mb-3 text-sm text-red-600">{error}</p>}
      {resultado && (
        <div className="mb-3 rounded border border-green-200 bg-green-50 p-3 text-sm text-green-900">
          <p className="font-medium">{textoDelResultado(resultado)}</p>
          {resultado.personas.length > 0 && (
            <>
              <p className="mt-2">Avísales que ya no tienen ese turno:</p>
              <ul className="ml-5 list-disc">
                {resultado.personas.map((p, i) => (
                  <li key={i}>{p.nombre} · {diaSemanaYFecha(p.fecha)} · {p.pdv ?? '—'}</li>
                ))}
              </ul>
            </>
          )}
          {resultado.bloqueadas.length > 0 && (
            <>
              <p className="mt-2">No se dieron de baja (corrígelos en Validación):</p>
              <ul className="ml-5 list-disc">
                {resultado.bloqueadas.map((b) => (
                  <li key={b.id}>{diaSemanaYFecha(b.fecha)} · {b.pdv ?? '—'}</li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
      {cargando && <p className="text-gray-500">Cargando turnos…</p>}
      {!cargando && !error && jornadas.length === 0 && (
        <p className="text-gray-500">Esta campaña no tiene turnos.</p>
      )}

      {jornadas.length > 0 && (
        <BarraFiltros onLimpiar={() => setFiltros(FILTROS_VACIOS)} mostrarLimpiar={hayFiltros(filtros)}>
          <FiltroTexto etiqueta="Persona" valor={filtros.texto} onChange={set('texto')} placeholder="Nombre de quien tiene el turno" />
          <FiltroFecha etiqueta="Desde" valor={filtros.desde} onChange={set('desde')} />
          <FiltroFecha etiqueta="Hasta" valor={filtros.hasta} onChange={set('hasta')} />
          <FiltroSelect etiqueta="PDV" valor={filtros.pdv} onChange={set('pdv')} opciones={opcionesPdv} todas="Todas las tiendas" />
          <FiltroSelect
            etiqueta="Horario" valor={filtros.horario} onChange={set('horario')}
            opciones={comoOpciones(opcionesDe(filas, 'horario'))} todas="Todos los horarios"
          />
          <FiltroSelect
            etiqueta="Slot" valor={filtros.slot} onChange={set('slot')}
            opciones={comoOpciones(opcionesDe(filas, 'slot'))} todas="Todos los slots"
          />
          <FiltroSelect
            etiqueta="Estado" valor={filtros.estado} onChange={set('estado')}
            opciones={ESTADOS.map((e) => ({ valor: e, etiqueta: ETIQUETA_ESTADO[e] }))} todas="Todos"
          />
        </BarraFiltros>
      )}

      {!cargando && jornadas.length > 0 && (
        <div className="mb-2 flex flex-wrap items-center gap-3 text-sm">
          <label className="flex items-center gap-2">
            <input
              ref={casillaTodo}
              type="checkbox"
              checked={estadoTodo === 'todas'}
              disabled={visibles.every((j) => j.conMarca)}
              onChange={() => setSel((s) => alternarTodo(visibles, s))}
            />
            Seleccionar todo lo filtrado
          </label>
          <span className="text-gray-600">
            {visibles.length} turno(s) a la vista{hayFiltros(filtros) && ` de ${jornadas.length}`} ·{' '}
            <b>{aDarDeBaja.length} seleccionado(s)</b>
          </span>
          <button
            type="button"
            disabled={aDarDeBaja.length === 0}
            onClick={() => { setResultado(null); setAviso(''); setConfirmando(true); }}
            className="ml-auto rounded bg-red-600 px-3 py-1.5 font-medium text-white disabled:opacity-40"
          >
            Dar de baja ({aDarDeBaja.length})
          </button>
        </div>
      )}

      {!cargando && visibles.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full rounded border text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-gray-500">
                <th className="w-8 px-3 py-2" aria-label="Seleccionar" />
                <th className="px-3 py-2">Fecha</th>
                <th className="px-3 py-2">PDV</th>
                <th className="px-3 py-2">Horario</th>
                <th className="px-3 py-2">Slot</th>
                <th className="px-3 py-2">Persona</th>
                <th className="px-3 py-2">Estado</th>
              </tr>
            </thead>
            <tbody>
              {visibles.map((j) => {
                const estado = estadoParaBaja(j);
                return (
                  <tr key={j.id} className={`border-t ${sel.has(j.id) && !j.conMarca ? 'bg-red-50' : ''}`}>
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        aria-label={`Seleccionar ${j.date} ${j.pdv?.code ?? ''}`}
                        checked={sel.has(j.id) && !j.conMarca}
                        disabled={j.conMarca}
                        title={j.conMarca ? 'Tiene marca de asistencia: se corrige en Validación' : undefined}
                        onChange={() => alternar(j.id)}
                      />
                    </td>
                    <td className="whitespace-nowrap px-3 py-2">{diaSemanaYFecha(j.date)}</td>
                    <td className="px-3 py-2">{j.pdv ? `${j.pdv.code} — ${j.pdv.name}` : '—'}</td>
                    <td className="whitespace-nowrap px-3 py-2">{hhmm(j.start_time)}–{hhmm(j.end_time)}</td>
                    <td className="px-3 py-2">{j.slot_index}</td>
                    <td className="px-3 py-2">{j.personas.map((p) => `${p.nombre} (${p.estado})`).join(', ') || '—'}</td>
                    <td className={`px-3 py-2 ${estado === 'con_marca' ? 'text-gray-400' : ''}`}>{ETIQUETA_ESTADO[estado]}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {!cargando && jornadas.length > 0 && visibles.length === 0 && (
        <p className="text-gray-500">Ningún turno coincide con los filtros.</p>
      )}

      <div className="mt-6">
        <button type="button" onClick={() => setVerBajas((v) => !v)} className="text-sm font-medium text-blue-700 underline">
          {verBajas ? 'Ocultar' : 'Ver'} dados de baja ({bajas.length})
        </button>
        {verBajas && (
          bajas.length === 0 ? (
            <p className="mt-2 text-sm text-gray-500">No hay turnos dados de baja en esta campaña.</p>
          ) : (
            <div className="mt-2 overflow-x-auto">
              <table className="w-full rounded border text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left text-gray-500">
                    <th className="px-3 py-2">Fecha</th>
                    <th className="px-3 py-2">PDV</th>
                    <th className="px-3 py-2">Horario</th>
                    <th className="px-3 py-2">Motivo</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {bajas.map((b) => (
                    <tr key={b.id} className="border-t">
                      <td className="whitespace-nowrap px-3 py-2">{diaSemanaYFecha(b.fecha)}</td>
                      <td className="px-3 py-2">{b.pdv ? `${b.pdv.code} — ${b.pdv.name}` : '—'}</td>
                      <td className="whitespace-nowrap px-3 py-2">{hhmm(b.start_time)}–{hhmm(b.end_time)}</td>
                      <td className="px-3 py-2 text-gray-600">{b.motivo}</td>
                      <td className="px-3 py-2 text-right">
                        <button type="button" onClick={() => void reactivar(b)} className="rounded border px-3 py-1">
                          Reactivar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>

      {confirmando && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white p-5 shadow-xl">
            <h3 className="text-lg font-semibold">Dar de baja {resumen.total} turno(s)</h3>
            <p className="mt-1 text-sm text-gray-600">
              Desaparecen de todos los paneles y ningún horario los vuelve a crear. Se puede deshacer con «Reactivar».
            </p>
            {resumen.personas.length > 0 && (
              <div className="mt-3 rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                <p className="font-medium">{resumen.conPersona} tienen persona asignada; hay que avisarles:</p>
                <ul className="ml-5 mt-1 list-disc">
                  {resumen.personas.slice(0, MAX_NOMBRES).map((n, i) => <li key={i}>{n}</li>)}
                </ul>
                {resumen.personas.length > MAX_NOMBRES && (
                  <p className="mt-1">y {resumen.personas.length - MAX_NOMBRES} más (salen todos al terminar).</p>
                )}
              </div>
            )}
            <label className="mt-3 block text-sm">
              <span className="font-medium">Motivo</span> <span className="text-gray-500">(obligatorio)</span>
              <textarea
                className="mt-1 w-full rounded border px-3 py-2"
                rows={3}
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                placeholder="Ej.: el cliente canceló por inventario"
                autoFocus
              />
            </label>
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={() => setConfirmando(false)} disabled={enviando} className="rounded border px-3 py-1.5">
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => void confirmarBaja()}
                disabled={enviando || motivo.trim() === ''}
                className="rounded bg-red-600 px-3 py-1.5 font-medium text-white disabled:opacity-40"
              >
                {enviando ? 'Dando de baja…' : `Dar de baja ${resumen.total}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
